import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Database, Shield, FileCheck, CheckCircle2, Play } from 'lucide-react';
import HeroSection from '../../components/HeroSection';
import ancileDataDiscoveryImage from '../../assets/images/ancile-data-discovery-and-classification.png';

const FeatureSection = ({ title, description, image, isReversed = false, features }) => {
  const Content = () => (
    <div className="flex-1 space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
      <p className="text-lg text-gray-600">{description}</p>
      <ul className="space-y-4">
        {features.map((feature, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center gap-3 text-gray-600"
          >
            <div className="p-2 bg-[#EBF3FF] rounded-lg">
              <feature.icon className="w-5 h-5 text-[#4B83F2]" />
            </div>
            <span>{feature.text}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );

  const Image = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="flex-1"
    >
      <img
        src={image}
        alt={title}
        className="w-full h-auto"
      />
    </motion.div>
  );

  return (
    <div className={`flex flex-col md:flex-row gap-12 items-center py-16 ${
      isReversed ? 'md:flex-row-reverse' : ''
    }`}>
      <Content />
      <Image />
    </div>
  );
};

const DiscoveryCapabilityCard = ({ title, description, icon: Icon, features }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-[#EBF3FF] rounded-xl">
          <Icon className="w-6 h-6 text-[#4B83F2]" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      <ul className="space-y-3">
        {features.map((feature, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center gap-3 text-gray-600"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <span>{feature}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
};

const DataDiscoveryAndClassification = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    organization: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    // Nothing needed here
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the API URL from environment variable
      const scriptURL = "https://script.google.com/macros/s/AKfycbz_D9Ta8CKoZQoBrariausbsDVfoy080NIgDSFLd9SxVGnMRFRoCGXtWzq6JCS2fY0p/exec";
      
      // Create a FormData object
      const form = new FormData();
      form.append('timestamp', new Date().toISOString());
      form.append('videoId', 'data-classification');
      form.append('firstName', formData.username.split(' ')[0] || '');
      form.append('lastName', formData.username.split(' ').slice(1).join(' ') || '');
      form.append('email', formData.email);
      form.append('phoneNumber', formData.phone);
      form.append('organizationName', formData.organization);
      form.append('comments', '');
      
      // Use URLSearchParams for compatibility
      const params = new URLSearchParams();
      for (const pair of form.entries()) {
        params.append(pair[0], pair[1]);
      }
      
      // Create a URL with query string
      const url = scriptURL + '?' + params.toString();
      
      // Use a simple image approach to avoid CORS
      const img = document.createElement('img');
      img.style.display = 'none';
      img.src = url;
      document.body.appendChild(img);
      
      // Remove the image after a delay
      setTimeout(() => {
        document.body.removeChild(img);
      }, 5000);
    } catch (error) {
      // Handle error silently
    }
    
    // Continue with showing the video regardless of API success
    setFormSubmitted(true);
    setShowVideo(true);
    setShowModal(false);
  };

  const handleVideoClick = () => {
    if (!showVideo) {
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <HeroSection
        title="Data Discovery & Classification"
        subtitle="Automatically identify and classify sensitive data across your organization"
        image={ancileDataDiscoveryImage}
        hideImage={window.innerWidth < 768}
      />
      
      <div className="container mx-auto px-4 py-16">
        <FeatureSection
          title="Intelligent Data Discovery"
          description="Our AI-powered platform automatically scans and identifies sensitive data across your entire data landscape, providing comprehensive visibility into your data assets."
          image="/assets/images/platform/data-discovery-and-classification.png"
          features={[
            { icon: Search, text: "Automated sensitive data scanning" },
            { icon: Database, text: "Multi-source data discovery" },
            { icon: Shield, text: "Real-time data mapping" },
            { icon: FileCheck, text: "Custom classification rules" }
          ]}
        />

        <FeatureSection
          title="Advanced Classification"
          description="Leverage machine learning and pattern recognition to accurately classify data based on content, context, and metadata."
          image="/assets/images/platform/data-discovery-and-classification-2.png"
          isReversed={true}
          features={[
            { icon: Search, text: "AI-powered classification" },
            { icon: Database, text: "Context-aware analysis" },
            { icon: Shield, text: "Pattern recognition" },
            { icon: FileCheck, text: "Automated tagging" }
          ]}
        />

        {/* Key Features Section */}
        <div className="py-16 bg-gradient-to-b relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Advanced Discovery & Classification Capabilities
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our platform provides comprehensive data discovery and classification
                capabilities to help you understand and protect your data assets.
              </p>
            </motion.div>

            <div className="flex flex-col items-center gap-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                <DiscoveryCapabilityCard
                  title="Data Classification"
                  description="Intelligent data classification and categorization."
                  icon={FileCheck}
                  features={[
                    "Content-based classification",
                    "Context-aware analysis",
                    "Automated tagging",
                    "Classification rules"
                  ]}
                />
                <DiscoveryCapabilityCard
                  title="Sensitive Data Detection"
                  description="Advanced detection of sensitive and confidential data."
                  icon={Shield}
                  features={[
                    "PII detection",
                    "Financial data",
                    "Healthcare records",
                    "Custom data types"
                  ]}
                />
                <DiscoveryCapabilityCard
                  title="Data Inventory"
                  description="Comprehensive data asset inventory and tracking."
                  icon={Database}
                  features={[
                    "Asset discovery",
                    "Location tracking",
                    "Usage monitoring",
                    "Inventory management"
                  ]}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full md:max-w-[66%]">
                <DiscoveryCapabilityCard
                  title="Pattern Recognition"
                  description="Advanced pattern recognition for data classification."
                  icon={Database}
                  features={[
                    "Regex patterns",
                    "Machine learning models",
                    "Custom classifiers",
                    "Pattern validation"
                  ]}
                />
                <DiscoveryCapabilityCard
                  title="Data Scanning"
                  description="Comprehensive data scanning across multiple sources."
                  icon={Search}
                  features={[
                    "Multi-source scanning",
                    "Real-time monitoring",
                    "Incremental scanning",
                    "Custom scan rules"
                  ]}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Video Demo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="rounded-xl overflow-hidden shadow-lg">
            {!showVideo ? (
              <div 
                className="relative cursor-pointer group" 
                onClick={handleVideoClick}
              >
                <img 
                  src="/assets/images/platform/data-discovery-and-classification-2.png" 
                  alt="Data Classification Demo" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center group-hover:bg-opacity-50 transition-all">
                  <div className="bg-white bg-opacity-90 rounded-full p-5 shadow-lg transform transition-transform group-hover:scale-110">
                    <Play className="w-10 h-10 text-[#4B83F2]" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                  <h3 className="text-2xl font-bold text-white">Watch the Data Classification Demo</h3>
                </div>
              </div>
            ) : (
              <div>
                <video 
                  src="/assets/videos/data-classification.mp4" 
                  controls  
                  className="w-full h-auto"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>
        </motion.div>

        {/* Modal Form */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div 
              className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm" 
              onClick={closeModal}
            ></div>
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full mx-4 z-10 relative">
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Watch the Data Classification Demo
              </h3>
              <p className="text-gray-600 mb-6 text-center">
                Please provide your information to access our demo video.
              </p>
              
              <form onSubmit={handleSubmit} className="w-full">
                <div className="grid grid-cols-1 gap-4 mb-6">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4B83F2]"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4B83F2]"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4B83F2]"
                    />
                  </div>
                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4B83F2]"
                    />
                  </div>
                </div>
                <div className="flex justify-center">
                  <button 
                    type="submit" 
                    className="bg-[#4B83F2] hover:bg-[#3A72E1] text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-md w-full"
                  >
                    Watch Video
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataDiscoveryAndClassification; 